import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import { fetchEmployerProfile } from '../store/employerSlice';
import { FaSpinner } from 'react-icons/fa';

interface RequireEmployerProfileProps {
  children: React.ReactElement;
}

const RequireEmployerProfile: React.FC<RequireEmployerProfileProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { employerProfile, profileLoading, profileError } = useSelector((state: RootState) => state.employer);

  useEffect(() => {
    if (user?.role === 'employer' && !employerProfile && !profileLoading && !profileError) {
      dispatch(fetchEmployerProfile());
    }
  }, [dispatch, user, employerProfile, profileLoading, profileError]);

  // Only enforce for employers
  if (user?.role !== 'employer') return children;

  // Keep the employer in a stable loading state while the first profile
  // request starts; otherwise the initial empty Redux state redirects early.
  if (!employerProfile && !profileError) {
    return (
      <div
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        <FaSpinner className="text-4xl animate-spin text-blue-600" aria-hidden="true" />
        <span className="sr-only">Checking your employer profile.</span>
      </div>
    );
  }

  // If error fetching (temporary), don't block
  if (profileError && profileError !== 'not_found') return children;

  // No profile exists — redirect to setup
  if (!employerProfile) {
    return <Navigate to="/employer/setup" state={{ from: location.pathname, onboarding: true }} replace />;
  }

  return children;
};

export default RequireEmployerProfile;
